import React, { useState, cloneElement, Component } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { PlaneIcon, MapIcon, CompassIcon, SparklesIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
export function TravelPage() {
  const [formData, setFormData] = useState({
    name: '',
    contactPref: '',
    contactDetails: '',
    destination: '',
    tripType: '',
    budget: '',
    dates: '',
    travelers: '',
    budgetBased: '',
    priorities: '',
    extraDetails: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent('Travel Request - Trippin with Mandy');
    const body = encodeURIComponent(`
Name: ${formData.name}
Contact Preference: ${formData.contactPref}
Contact Details: ${formData.contactDetails}
Destination: ${formData.destination}
Trip Type: ${formData.tripType}
Budget: ${formData.budget}
Dates: ${formData.dates}
Travelers: ${formData.travelers}
Budget-based destination choice: ${formData.budgetBased}
Priorities: ${formData.priorities}
Extra Details: ${formData.extraDetails}
    `.trim());
    window.location.href = `mailto:trippinwithmandy@gmail.com?subject=${subject}&body=${body}`;
  };
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-608">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-sky-50 to-white" data-id="element-609">
        <SparkleEffect count={90} data-id="element-610" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-611">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-612">
            Travel with <span className="shimmer-text" data-id="element-613">Mandy</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-614">
            Because everybody deserves to get the hell out sometimes.
          </motion.p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-615">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="prose prose-lg prose-plum mx-auto text-plum-700 leading-relaxed text-center" data-id="element-616">
          <p className="text-2xl font-heading text-plum-700 mb-8 font-bold" data-id="element-617">
            I believe travel is healing.
          </p>
          <p className="mb-6 font-medium" data-id="element-618">
            Getting out of your everyday environment, seeing something new,
            breathing different air — it changes you. It resets your nervous
            system and reminds you that the world is bigger than whatever you're
            going through right now.
          </p>
          <p className="mb-6 font-medium" data-id="element-619">
            I don't care if your budget is $200 or $2,000. I will find you
            something. A weekend getaway, a road trip, a beach somewhere —
            everyone deserves to escape.
          </p>
          <p className="text-xl text-cta-500 font-bold mt-8" data-id="element-620">
            Tell me your budget, tell me your dream, and let me make it happen.
          </p>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-blush-50" data-id="element-621">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-622">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-16" data-id="element-623">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-624">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-625" />

            {[{
            icon: <MapIcon data-id="element-626" />,
            title: '1. Tell Me Your Budget',
            desc: 'No judgment, just honesty. We work with what you have.'
          }, {
            icon: <CompassIcon data-id="element-627" />,
            title: '2. I Find Your Perfect Trip',
            desc: 'From weekend getaways to week-long adventures, I handle the planning.'
          }, {
            icon: <PlaneIcon data-id="element-628" />,
            title: '3. You Go Live Your Best Life',
            desc: 'Pack your bags, escape, and come back recharged.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-629">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-630">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-631">
                  {step.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-632">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Travel Types */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-633">
        <div className="text-center mb-16" data-id="element-634">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-4" data-id="element-635">
            What We Can Plan
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-636">
          {[{
          title: 'Budget Getaways',
          desc: 'Weekend trips and local escapes under $500.',
          color: 'bg-blush-50 border-blush-100'
        }, {
          title: 'Healing Retreats',
          desc: 'Wellness-focused travel experiences to reset your soul.',
          color: 'bg-lavender-50 border-lavender-100'
        }, {
          title: 'Adventure Trips',
          desc: 'For when you need to feel alive and push your boundaries.',
          color: 'bg-sky-50 border-sky-100'
        }, {
          title: 'Group Travel',
          desc: 'Coming soon: group trips with the community. We rise together.',
          color: 'bg-white border-plum-100',
          badge: 'Coming Soon'
        }].map((type, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`p-8 rounded-3xl border ${type.color} shadow-sm relative`} data-id="element-637">
              {type.badge && <div className="absolute top-4 right-4 px-2 py-1 bg-plum-100 text-plum-700 text-xs font-bold rounded-md uppercase" data-id="element-638">
                  {type.badge}
                </div>}
              <SparklesIcon className="w-6 h-6 text-cta-500 mb-4" data-id="element-639" />
              <h3 className="font-heading text-xl font-bold text-plum-700 mb-2" data-id="element-640">
                {type.title}
              </h3>
              <p className="text-plum-500 font-medium text-sm" data-id="element-641">{type.desc}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Travel Questionnaire Form */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-white to-blush-50" data-id="element-642">
        <SparkleEffect count={80} data-id="element-643" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-644">
          <div className="text-center mb-12" data-id="element-645">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-700 mb-4 flex items-center justify-center gap-3" data-id="element-646">
              Let Me Plan Your Escape ✈️
            </h2>
            <p className="text-lg text-plum-500 font-medium max-w-2xl mx-auto" data-id="element-647">
              Tell me what you're looking for—even if you don't know exactly
              where you want to go yet. Give me your budget, your vibe, and your
              must-haves… and I'll build something just for you 💕
            </p>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white/60 backdrop-blur-md p-8 md:p-12 rounded-[3rem] shadow-xl border border-white relative" data-id="element-648">
            <div className="absolute -top-6 -right-6 rotate-12" data-id="element-649">
              <GlitterHeart size={40} className="opacity-80" data-id="element-650" />
            </div>
            <div className="absolute -bottom-4 -left-4 -rotate-12" data-id="element-651">
              <GlitterHeart size={30} variant="light" className="opacity-60" data-id="element-652" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10" data-id="element-653">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-654">
                {/* Name */}
                <div className="md:col-span-2" data-id="element-655">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-656">
                    Name
                  </label>
                  <input type="text" name="name" required value={formData.name} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Your name" data-id="element-657" />
                </div>

                {/* Contact Preference */}
                <div className="md:col-span-2" data-id="element-658">
                  <label className="block text-sm font-bold text-plum-700 mb-3" data-id="element-659">
                    Best way to contact you
                  </label>
                  <div className="flex flex-wrap gap-4" data-id="element-660">
                    {['Phone Call', 'Text Message', 'Email'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer group" data-id="element-661">
                        <input type="radio" name="contactPref" value={option} required onChange={handleInputChange} className="w-5 h-5 text-cta-500 border-blush-300 focus:ring-cta-500 cursor-pointer" data-id="element-662" />
                        <span className="text-plum-600 font-medium group-hover:text-plum-800 transition-colors" data-id="element-663">
                          {option}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="md:col-span-2" data-id="element-664">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-665">
                    Enter your contact details
                  </label>
                  <input type="text" name="contactDetails" required value={formData.contactDetails} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Phone number or email" data-id="element-666" />
                </div>

                {/* Destination */}
                <div className="md:col-span-2" data-id="element-667">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-668">
                    Where are you thinking about going? (optional)
                  </label>
                  <input type="text" name="destination" value={formData.destination} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="E.g., Mexico, Europe, or 'Anywhere warm!'" data-id="element-669" />
                </div>

                {/* Trip Type */}
                <div className="md:col-span-2" data-id="element-670">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-671">
                    What kind of trip are you looking for?
                  </label>
                  <input type="text" name="tripType" required value={formData.tripType} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Relaxing, adventure, cruise, romantic, family, solo, etc." data-id="element-672" />
                </div>

                {/* Budget */}
                <div data-id="element-673">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-674">
                    Budget Range
                  </label>
                  <select name="budget" required value={formData.budget} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all text-plum-700" data-id="element-675">
                    <option value="" disabled data-id="element-676">
                      Select a budget
                    </option>
                    <option value="Under $500" data-id="element-677">Under $500</option>
                    <option value="$500–$1000" data-id="element-678">$500–$1000</option>
                    <option value="$1000+" data-id="element-679">$1000+</option>
                    <option value="Custom" data-id="element-680">Custom</option>
                  </select>
                </div>

                {/* Travelers */}
                <div data-id="element-681">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-682">
                    Who is traveling?
                  </label>
                  <select name="travelers" required value={formData.travelers} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all text-plum-700" data-id="element-683">
                    <option value="" disabled data-id="element-684">
                      Select travelers
                    </option>
                    <option value="Just me" data-id="element-685">Just me</option>
                    <option value="Couple" data-id="element-686">Couple</option>
                    <option value="Family" data-id="element-687">Family</option>
                    <option value="Group" data-id="element-688">Group</option>
                  </select>
                </div>

                {/* Dates */}
                <div className="md:col-span-2" data-id="element-689">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-690">
                    Travel Dates or Flexibility
                  </label>
                  <input type="text" name="dates" required value={formData.dates} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Exact dates or 'flexible in October'" data-id="element-691" />
                </div>

                {/* Budget-based option */}
                <div className="md:col-span-2 bg-blush-50/80 p-5 rounded-2xl border border-blush-100" data-id="element-692">
                  <label className="block text-sm font-bold text-plum-800 mb-3" data-id="element-693">
                    Are you looking for a budget-based trip where I choose the
                    destination based on your budget?
                  </label>
                  <div className="flex gap-6" data-id="element-694">
                    {['Yes', 'No'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer group" data-id="element-695">
                        <input type="radio" name="budgetBased" value={option} required onChange={handleInputChange} className="w-5 h-5 text-cta-500 border-blush-300 focus:ring-cta-500 cursor-pointer" data-id="element-696" />
                        <span className="text-plum-700 font-bold group-hover:text-plum-900 transition-colors" data-id="element-697">
                          {option}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Priorities */}
                <div className="md:col-span-2" data-id="element-698">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-699">
                    What matters most for this trip?
                  </label>
                  <input type="text" name="priorities" required value={formData.priorities} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Price, luxury, experience, location, etc." data-id="element-700" />
                </div>

                {/* Extra Details */}
                <div className="md:col-span-2" data-id="element-701">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-702">
                    Anything else you want me to know?
                  </label>
                  <textarea name="extraDetails" rows={4} value={formData.extraDetails} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all resize-none" placeholder="Any special requests, accessibility needs, or absolute dealbreakers..." data-id="element-703" />
                </div>
              </div>

              <div className="pt-6 text-center" data-id="element-704">
                <button type="submit" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-12 py-4 rounded-full bg-cta-500 text-white font-bold text-xl hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-1 hover:scale-105" data-id="element-705">
                  Build My Trip 💖
                </button>
                <p className="mt-4 text-sm font-medium text-plum-500" data-id="element-706">
                  I'll personally review your request and reach out with options
                  that fit your vibe and your budget.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>;
}